import {useConfirm} from '@primer/react'
import {useEffect} from 'react'

// Trigger browser's native discard changes confirmation when navigating away.
function useNavigateAwayDiscardConfirmation(hasUnsavedChanges: boolean) {
  useEffect(() => {
    document.body.setAttribute('data-turbo', 'false')
    window.onbeforeunload = hasUnsavedChanges
      ? e => {
          e.returnValue = 'You have unsaved changes. Are you sure you want to leave this page?'
        }
      : null

    return () => {
      document.body.removeAttribute('data-turbo')
      window.onbeforeunload = null
    }
  }, [hasUnsavedChanges])
}

// Handle discard changes confirmation within the app.
interface DiscardConfirmationHookProps {
  hasUnsavedChanges: boolean
  onConfirm: () => void
}
export function useDiscardConfirmation({hasUnsavedChanges, onConfirm}: DiscardConfirmationHookProps) {
  useNavigateAwayDiscardConfirmation(hasUnsavedChanges)

  const confirm = useConfirm()
  const canClose = async () => {
    if (hasUnsavedChanges) {
      return confirm({
        title: 'Discard changes?',
        content: 'You have unsaved changes. Are you sure you want to discard them?',
        cancelButtonContent: 'Keep editing',
        confirmButtonContent: 'Discard',
        confirmButtonType: 'danger',
      })
    } else {
      return true
    }
  }

  const discardWithConfirmation = async () => {
    if (!hasUnsavedChanges || (await canClose())) {
      onConfirm()
    }
  }

  return {canClose, discardWithConfirmation}
}
